import React, { useCallback, useEffect, useState } from 'react';
import { Bell, Edit3, Lock, Unlock } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import AdminBottomNav from '../../components/AdminBottomNav';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DAYS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];
const MONTHS_FULL = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

function getDaysInMonth(year, month) { return new Date(year, month + 1, 0).getDate(); }
function getFirstDayOfWeek(year, month) { return (new Date(year, month, 1).getDay() + 6) % 7; }

export default function AdminCalendarPage() {
  const today = new Date();
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());
  const [rangeStart, setRangeStart] = useState(null);
  const [rangeEnd, setRangeEnd] = useState(null);
  const [blockedDates, setBlockedDates] = useState([]);
  const [saving, setSaving] = useState(false);

  const logAdminCalendarDebug = useCallback((message, error) => {
    if (process.env.NODE_ENV === 'development') {
      console.error(message, error);
    }
  }, []);

  const fetchBlockedDates = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/venue/blocked-dates`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBlockedDates(res.data);
    } catch (err) {
      logAdminCalendarDebug('Failed to load blocked dates:', err);
      toast.error('Failed to load blocked dates');
    }
  }, [logAdminCalendarDebug]);

  useEffect(() => { fetchBlockedDates(); }, [fetchBlockedDates]);

  const daysInMonth = getDaysInMonth(viewYear, viewMonth);
  const firstDay = getFirstDayOfWeek(viewYear, viewMonth);

  const makeDateStr = (day) => `${viewYear}-${String(viewMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

  const handleDayClick = (day) => {
    const dateStr = makeDateStr(day);
    if (!rangeStart || (rangeStart && rangeEnd)) {
      setRangeStart(dateStr);
      setRangeEnd(null);
    } else {
      if (dateStr >= rangeStart) { setRangeEnd(dateStr); }
      else { setRangeEnd(rangeStart); setRangeStart(dateStr); }
    }
  };

  const isInSelection = (day) => {
    if (!rangeStart) return false;
    const dateStr = makeDateStr(day);
    if (!rangeEnd) return dateStr === rangeStart;
    return dateStr >= rangeStart && dateStr <= rangeEnd;
  };

  const isBlocked = (day) => {
    const dateStr = makeDateStr(day);
    return blockedDates.some(b => b.start_date <= dateStr && b.end_date >= dateStr);
  };

  const isSelStart = (day) => makeDateStr(day) === rangeStart;
  const isSelEnd = (day) => makeDateStr(day) === rangeEnd;

  const prevMonth = () => { if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); } else setViewMonth(m => m - 1); };
  const nextMonth = () => { if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); } else setViewMonth(m => m + 1); };

  const handleBlockDates = async () => {
    if (!rangeStart) return;
    const end = rangeEnd || rangeStart;
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/venue/blocked-dates`, {
        start_date: rangeStart, end_date: end, reason: 'Closed by admin',
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Dates blocked: ${rangeStart} to ${end}`);
      setRangeStart(null); setRangeEnd(null);
      fetchBlockedDates();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to block dates');
    } finally { setSaving(false); }
  };

  const handleOpenDates = async () => {
    if (!rangeStart) return;
    const end = rangeEnd || rangeStart;
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/venue/blocked-dates`, {
        data: { start_date: rangeStart, end_date: end },
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`Dates opened: ${rangeStart} to ${end}`);
      setRangeStart(null); setRangeEnd(null);
      fetchBlockedDates();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to open dates');
    } finally { setSaving(false); }
  };

  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const hasSelection = !!rangeStart;
  // Check if selected range overlaps with any blocked dates
  const selectionHasBlocked = hasSelection && (() => {
    const end = rangeEnd || rangeStart;
    return blockedDates.some(b => b.start_date <= end && b.end_date >= rangeStart);
  })();

  return (
    <div className="app-shell" data-testid="admin-calendar-page" style={{ minHeight: '100dvh', backgroundColor: 'var(--app-bg)', display: 'flex', flexDirection: 'column', alignItems: 'center', paddingBottom: 120 }}>
      {/* Header */}
      <div style={{ width: '100%', maxWidth: 390, display: 'flex', justifyContent: 'flex-end', padding: '16px 20px 0' }}>
        <button style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}><Bell size={26} color="var(--text)" strokeWidth={1.3} /></button>
      </div>
      <img src="/allure-logo.png" alt="Allure" style={{ width: 100, height: 89, objectFit: 'contain' }} />

      {/* Title */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8 }}>
        <h2 style={{ fontFamily: "'Inria Serif', serif", fontSize: 26, fontWeight: 700, color: 'var(--text)' }}>Calendar</h2>
        <Edit3 size={22} color="var(--text)" />
      </div>

      {/* Calendar */}
      <div style={{ width: 'calc(100% - 32px)', maxWidth: 360, marginTop: 16, backgroundColor: 'var(--surface)', borderRadius: 16, padding: '16px 12px', border: '1px solid var(--card-border)' }}>
        {/* Month nav */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
          <button onClick={prevMonth} style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: "'Inria Serif', serif", fontSize: 22, color: 'var(--text)' }}>&#8249;</button>
          <span style={{ fontFamily: "'Inria Serif', serif", fontSize: 20, fontWeight: 700, color: 'var(--text)' }}>{MONTHS_FULL[viewMonth]} {viewYear}</span>
          <button onClick={nextMonth} style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: "'Inria Serif', serif", fontSize: 22, color: 'var(--text)' }}>&#8250;</button>
        </div>

        {/* Day headers */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2, marginBottom: 6 }}>
          {DAYS.map(d => (
            <div key={d} style={{ textAlign: 'center', fontFamily: "'Inria Serif', serif", fontSize: 14, fontWeight: 700, color: 'var(--text-3)', padding: '4px 0' }}>{d}</div>
          ))}
        </div>

        {/* Day cells */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}>
          {cells.map((day, i) => {
            if (day === null) return <div key={`empty-${viewYear}-${viewMonth}-${i}`} />;
            const inSel = isInSelection(day);
            const blocked = isBlocked(day);
            const start = isSelStart(day);
            const end = isSelEnd(day);
            const isToday = makeDateStr(day) === todayStr;
            const dayKey = `admin-cal-${makeDateStr(day)}`;

            // Determine background color
            let bg = 'transparent';
            let color = 'var(--text)';
            let fontWeight = 400;
            let borderRad = '50%';

            if (inSel) {
              bg = start || end ? '#E53935' : '#F28B82';
              color = '#fff';
              fontWeight = 700;
              borderRad = start ? '50% 0 0 50%' : end ? '0 50% 50% 0' : '0';
            } else if (blocked) {
              bg = '#FFD6D6';
              color = '#C62828';
              fontWeight = 600;
            }

            return (
              <button
                key={dayKey}
                onClick={() => handleDayClick(day)}
                data-testid={`cal-day-${day}`}
                style={{
                  width: '100%', aspectRatio: '1', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontFamily: "'Inria Serif', serif", fontSize: 16, fontWeight,
                  color, backgroundColor: bg, borderRadius: borderRad,
                  border: isToday && !inSel ? '2px solid #E53935' : 'none',
                  cursor: 'pointer', padding: 0, position: 'relative',
                }}
              >
                {day}
                {blocked && !inSel && (
                  <div style={{ position: 'absolute', bottom: 2, width: 4, height: 4, borderRadius: '50%', backgroundColor: '#C62828' }} />
                )}
              </button>
            );
          })}
        </div>

        {/* Range display */}
        {rangeStart && (
          <div style={{ marginTop: 12, textAlign: 'center', fontFamily: "'Inria Serif', serif", fontSize: 16, color: 'var(--text-2)' }}>
            <span style={{ fontWeight: 700 }}>From:</span> {rangeStart}
            {rangeEnd && <><span style={{ margin: '0 8px' }}>→</span><span style={{ fontWeight: 700 }}>Until:</span> {rangeEnd}</>}
          </div>
        )}
      </div>

      {/* Block / Open buttons */}
      {hasSelection && (
        <div style={{ display: 'flex', gap: 12, marginTop: 16 }}>
          <button
            onClick={handleBlockDates}
            disabled={saving}
            data-testid="cal-block-btn"
            style={{
              display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px',
              fontFamily: "'Inria Serif', serif", fontSize: 18, fontWeight: 700,
              color: '#fff', backgroundColor: '#E53935',
              border: 'none', borderRadius: 12, cursor: 'pointer',
              boxShadow: '0 2px 8px rgba(229,57,53,0.25)',
              opacity: saving ? 0.6 : 1,
            }}
          >
            <Lock size={18} /> Block Dates
          </button>

          {selectionHasBlocked && (
            <button
              onClick={handleOpenDates}
              disabled={saving}
              data-testid="cal-open-btn"
              style={{
                display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px',
                fontFamily: "'Inria Serif', serif", fontSize: 18, fontWeight: 700,
                color: '#fff', backgroundColor: '#00B341',
                border: 'none', borderRadius: 12, cursor: 'pointer',
                boxShadow: '0 2px 8px rgba(0,179,65,0.25)',
                opacity: saving ? 0.6 : 1,
              }}
            >
              <Unlock size={18} /> Open Dates
            </button>
          )}
        </div>
      )}

      {/* Legend */}
      <div style={{ display: 'flex', gap: 16, marginTop: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={{ width: 14, height: 14, borderRadius: '50%', backgroundColor: '#FFD6D6', border: '1px solid #C62828' }} />
          <span style={{ fontFamily: "'Inria Serif', serif", fontSize: 14, color: 'var(--text-3)' }}>Blocked</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={{ width: 14, height: 14, borderRadius: '50%', border: '2px solid #E53935' }} />
          <span style={{ fontFamily: "'Inria Serif', serif", fontSize: 14, color: 'var(--text-3)' }}>Today</span>
        </div>
      </div>

      <AdminBottomNav />
    </div>
  );
}
